import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Sparkles, Network, ChevronRight } from "lucide-react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { errMsg } from "../lib/errors";

const EXAMPLES = [
  {
    label: "Single posterior implant",
    text: "Healthy 42yo female missing #14 due to vertical root fracture. Adjacent teeth virgin. Adequate bone on CBCT. Wants implant.",
  },
  {
    label: "Smile + perio + implant",
    text: "58yo male, smoker, hopeless #19 (deep furcation, mobility II, 9mm probing depths circumferentially). Maxillary anteriors #6-#11 generalized recession 2-3mm with esthetic concerns and slight wear. Wants implant for #19 and smile improvement.",
  },
  {
    label: "Full-arch reconstruction",
    text: "65yo female, terminal upper dentition with multiple hopeless teeth, wants fixed full-arch implant solution. CBCT shows adequate bone in anterior maxilla with bilateral sinus pneumatization. Patient has well-controlled diabetes (HbA1c 6.8). Needs immediate transition plan.",
  },
];

export default function TopologyAnalysisNew() {
  const nav = useNavigate();
  const [caseInput, setCaseInput] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const text = caseInput.trim();
    if (text.length < 30) {
      toast.error("Please describe the case in at least a sentence or two.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/topology-analysis", { case_input: text });
      const id = r.data?.id;
      if (!id) throw new Error("No analysis id returned");
      toast.success("Analysis started — opening live progress…");
      nav(`/topology-analysis/${id}`);
    } catch (e) {
      toast.error(errMsg(e, "Could not start topology analysis"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout>
      <div className="kicker">Adaptive Agentic Topology</div>
      <h1
        data-testid="topology-new-title"
        className="font-serif text-3xl text-[var(--pa-text)] mt-1 flex items-center gap-3"
      >
        <Network size={22} className="text-[var(--pa-gold)]" /> Topology Analysis
      </h1>
      <p className="mt-2 text-sm text-[var(--pa-text-muted)] max-w-2xl leading-relaxed">
        Describe the case in your own words. The classifier reads it, picks the
        right combination of clinical agents from the 11 modules, runs them in
        parallel where possible with forward-feeding context, then synthesizes
        a unified case report — typically <strong>2-4 minutes</strong> end to
        end. You'll see live progress here.
      </p>

      <div className="mt-7 max-w-3xl space-y-5">
        <div>
          <label className="kicker block mb-1">Case description</label>
          <textarea
            value={caseInput}
            onChange={(e) => setCaseInput(e.target.value)}
            rows={9}
            placeholder="Patient presents with…"
            data-testid="topology-input"
            className="w-full bg-[var(--pa-bg-2)] border border-[var(--pa-border)] focus:border-[var(--pa-gold)] focus:outline-none rounded text-sm text-[var(--pa-text)] p-3 leading-relaxed font-sans"
          />
          <div className="mt-1 flex justify-between text-[11px] text-[var(--pa-text-dim)]">
            <span>{caseInput.length} chars</span>
            <span>Tip: chief complaint, key findings, what the patient wants.</span>
          </div>
        </div>

        <div>
          <div className="kicker mb-2">Or try one of these examples</div>
          <div className="space-y-1.5">
            {EXAMPLES.map((ex, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setCaseInput(ex.text)}
                data-testid={`example-${i}`}
                className="w-full text-left text-xs bg-[var(--pa-bg-2)] border border-[var(--pa-border)] hover:border-[var(--pa-gold)]/60 transition rounded px-3 py-2 flex items-center gap-2"
              >
                <ChevronRight size={12} className="text-[var(--pa-gold)] shrink-0" />
                <span className="text-[var(--pa-gold)] font-semibold mr-2 shrink-0">
                  {ex.label}
                </span>
                <span className="text-[var(--pa-text-muted)] truncate">
                  {ex.text}
                </span>
              </button>
            ))}
          </div>
        </div>

        <button
          onClick={submit}
          disabled={busy}
          data-testid="topology-submit"
          className="flex items-center gap-2 bg-[var(--pa-gold)] text-[var(--pa-bg)] font-semibold px-6 py-3 rounded-md hover:bg-[var(--pa-gold-hover)] transition disabled:opacity-60"
        >
          {busy ? (
            <>
              <Loader2 size={14} className="animate-spin" /> Starting…
            </>
          ) : (
            <>
              <Sparkles size={14} /> Run topology analysis
            </>
          )}
        </button>
      </div>
    </Layout>
  );
}
